import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import { businessDetailsValidationSchema } from './businessSchema';
import {
  Form,
  Formik
} from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { setBusinessDetails } from '../../../api/vodaLend';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { FormSubmitHelper, SupportPopup, ErrorModalSection } from '../../../components';
import {
  FormContainer,
  FormField
} from '../../../constants';
import { useModal } from '../../../components/modalController/useModal';
import { useLoader } from '../../../components/Loader/useLoader';
import {
  mapValues,
  stripEmpty,
  replaceUndefined
} from '../../../helpers';
import { findFirstValidationError } from '../../../helpers/formik';
import { businessDetailsSubmit, useIsMobile } from '../../../recoil';
import { personalDetailsSubmit, fundingDetailsSubmit } from '../../../recoil';

import { useTealium } from '../../../tealium/useTealium';
import styles from './BusinessDetails.module.css';
import {
  getFormFields,
  businessDetailsKeys
} from './businessDetailsConstants';
import { FormBuilder } from '../../../components/FormBuilder';
import { ApplicationRoutes } from '../../../Router';
import { StatusCodes } from 'http-status-codes';
import { businessList, applications } from '../../../recoil';

export const BusinessDetails = () => {
  const { submitState, setSubmitState, backClick, submissionStates, nextClick } = useOutletContext();
  const [openModal, closeModal] = useModal();
  const [details, setDetails] = useRecoilState(businessDetailsSubmit);
  const [personalDetails] = useRecoilState(personalDetailsSubmit);
  const [fundingDetails] = useRecoilState(fundingDetailsSubmit);
  const isUnverifiedBusiness = details?.isBusinessVerified === false;
  const bussinessList = useRecoilValue(businessList);
  const appData = useRecoilValue(applications);
  const verifyRegNumber = sessionStorage.getItem('regNumber');
  const verifyBusinessName = sessionStorage.getItem('businessName');
  const verifyVatNumber = sessionStorage.getItem('vatNumber');
  const [openLoader, closeLoader] = useLoader();
  const formRef = React.useRef();
  const isMobile = useRecoilValue(useIsMobile);
  const businessType = details?.businessType || fundingDetails?.businessType;
  const timeInBusiness = details?.timeInBusiness || fundingDetails?.timeInBusiness;
  const revenueBracket = details?.revenueBracket || fundingDetails?.revenue;
  const hasApplications = appData?.applications?.length > 0;
  const hasSharedBusinessId = (localStorage.getItem('isNewBusiness') === 'true');
  const [isVatNumberPreFetched, setIsVatNumberPreFetched] = useState(false);
  const shouldShowBusinessDirectors = hasApplications && hasSharedBusinessId;
  const sharedBusinessIdDirector = localStorage.getItem('isNewBusiness') === 'true' && details?.sharedBusinessDirectors ? details?.sharedBusinessDirectors : '';
  const isSharedDirector = shouldShowBusinessDirectors && !details?.businessId ? '' : details?.sharedBusinessDirectors ? 'Yes' : 'No';
  const [sharedDirectors, setSharedDirectors] = useState(isSharedDirector);

  const formatBusinessList = (businessList) =>
    businessList?.businesses?.map(b => {
      if (b?.businessName && b?.businessId && b?.businessType !== 'Sole Proprietorship') {
        return {
          label: b.businessName,
          value: b.businessId
        };
      }
      return null;
    }).filter(Boolean) || [];

  const formattedList = shouldShowBusinessDirectors ? formatBusinessList(bussinessList) : [];

  const isDetailsCaptured = (details, businessDetailsKeys) => {
    const missingFields = businessDetailsKeys.filter(
      key => details[key] === null || details[key] === undefined || details[key] === ''
    );

    // Conditional check for tax identifiers
    const { businessType, regNumber, vatNumber } = details;

    const isSoleProp = businessType === 'Sole Proprietorship';

    const isVatValid = !isSoleProp || (vatNumber && vatNumber.trim() !== '');
    const isRegValid = isSoleProp || (regNumber && regNumber.trim() !== '');

    if (!isVatValid) { missingFields.push('vatNumber'); }
    if (!isRegValid) { missingFields.push('regNumber'); }

    return {
      result: missingFields.length === 0,
      missingFields
    };
  };

  const formDetails = useMemo(() => {

    if (isUnverifiedBusiness) {
      return {
        businessType: details?.businessType,
        regNumber: details?.regNumber,
        name: details?.name,
        vatNumber: details?.vatNumber
      };
    }

    return replaceUndefined({
      ...details,
      regNumber: details?.regNumber || verifyRegNumber,
      businessType,
      timeInBusiness,
      revenueBracket,
      unitNumber: details?.address?.unitNumber,
      street: details?.address?.street,
      suburb: details?.address?.suburb,
      postalCode: details?.address?.postalCode,
      city: details?.address?.city,
      province: details?.address?.province,
      industry: details?.industry,
      name: details?.name || verifyBusinessName,
      vatNumber: details?.vatNumber || verifyVatNumber,
      landline: details?.landline,
      website: details?.website || '',
      country: 'ZA',
      isPersonalAddress: details?.isPersonalAddress || false,
      buildingType: details?.address?.buildingType,
      businessId: details?.businessId,

      houseUnitNumber: details?.address?.unitNumber || '',
      flatUnitNumber: details?.address?.unitNumber || '',
      townUnitNumber: details?.address?.unitNumber || '',
      officeUnitNumber: details?.address?.unitNumber || '',

      flatBuilding: details?.address?.building,
      townBuilding: details?.address?.building,
      officeBuilding: details?.address?.building,

      houseStreet: details?.address?.street,
      flatStreet: details?.address?.street,
      townStreet: details?.address?.street,
      officeStreet: details?.address?.street,
      tradingAs: details?.tradingAs,
      differentBusinessName: details?.tradingAs ? true : false,

      sharedDirectors: sharedDirectors,
      sharedBusinessDirectors: details?.sharedBusinessDirectors || sharedBusinessIdDirector
    });

  }, [details, fundingDetails]);

  const { track, events } = useTealium();
  const location = useLocation();
  let formTouched = false;

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.businessDetails1.formStart());
  };

  useEffect(() => {
    if(details?.vatNumber) {
      setIsVatNumberPreFetched(true);
    }
    track(events.businessDetails1.pageView());
  }, []);

  const getValue = (formData, key, defaultValue = null) => formData[key] || defaultValue;
  const getUnitNumber = (formData) => formData.unitNumber || '';
  const getBuilding = (formData) => formData.building || '';
  const getStreet = (formData) => formData.street || '';

  const formatAddress = (formData) => ({
    buildingType: formData?.buildingType || '',
    suburb: getValue(formData, 'suburb'),
    city: getValue(formData, 'city'),
    province: getValue(formData, 'province'),
    postalCode: getValue(formData, 'postalCode'),
    country: 'South Africa',
    houseUnitNumber: getUnitNumber(formData),
    flatUnitNumber: getUnitNumber(formData),
    townUnitNumber: getUnitNumber(formData),
    officeUnitNumber: getUnitNumber(formData),
    flatBuilding: getBuilding(formData),
    townBuilding: getBuilding(formData),
    officeBuilding: getBuilding(formData),
    houseStreet: getStreet(formData),
    flatStreet: getStreet(formData),
    townStreet: getStreet(formData),
    officeStreet: getStreet(formData),
    street: formData?.flatStreet || formData?.houseStreet || formData?.townStreet || formData?.officeStreet,
    unitNumber: formData?.flatUnitNumber || formData?.houseUnitNumber || formData?.townUnitNumber || formData?.officeUnitNumber,
    building: formData?.flatBuilding || formData?.townBuilding || formData?.officeBuilding || ''
  });

  const formatBusiness = (formData) => ({
    businessType: businessType,
    name: formData.name || null,
    regNumber: formData.regNumber || null,
    vatNumber: formData.vatNumber || null,
    industry: formData.industry || null,
    isPersonalAddress: formData?.isPersonalAddress || false,
    differentBusinessName: formData?.differentBusinessName,
    tradingAs: formData.differentBusinessName ? formData.tradingAs : null,
    revenueBracket: formData.revenueBracket || null,
    timeInBusiness: formData.timeInBusiness || null,
    landline: formData.landline || null,
    website: formData.website || null,
    businessId: formData.businessId || null
  });

  const formatFormData = (formData) => {
    const address = formatAddress(formData);
    const business = formatBusiness(formData);
    const formattedData = {
      ...business, address,
      sharedDirectors: formData?.sharedDirectors,
      sharedBusinessDirectors: formData?.sharedDirectors === 'Yes' ? formData?.sharedBusinessDirectors || sharedBusinessIdDirector : ''
    };
    return mapValues(stripEmpty(formattedData));
  };

  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  const formSubmit = async (formData) => {
    track(events.businessDetails1.formEnd('next'));
    if (isUnverifiedBusiness) {
      setSubmitState(submissionStates.submitSuccess);
      if (businessType === 'Sole Proprietorship') {
        localStorage.setItem('loanApplicationStepKey', 'financialDetails');
        window.location.pathname = ApplicationRoutes.financialDetails;
      } else {
        window.location.pathname = ApplicationRoutes.businessDirectors;
      }
      return details;
    }
    openLoader();
    let businessDetailResponse;
    const businessDetailFormattedFormData = formatFormData({ ...formData });
    businessDetailResponse = await setBusinessDetails(businessDetailFormattedFormData);
    closeLoader();
    if ([StatusCodes.CREATED, StatusCodes.OK].includes(businessDetailResponse.status)) {

      setDetails(businessDetailResponse.data);
      if (businessType === 'Sole Proprietorship') {
        localStorage.setItem('loanApplicationStepKey', 'financialDetails');
        sessionStorage.removeItem('businessName');
        sessionStorage.removeItem('vatNumber');
        window.location.pathname = ApplicationRoutes.financialDetails;
        nextClick();
      } else {
        setSubmitState(submissionStates.submitSuccess);
        sessionStorage.removeItem('businessName');
        sessionStorage.removeItem('vatNumber');
        window.location.pathname = ApplicationRoutes.businessDirectors;
      }

    } else if (businessDetailResponse.data.error) {
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={businessDetailResponse?.traceId}
          error={businessDetailResponse?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
    }
    return businessDetailResponse;
  };

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }

    track(events.error('form', location.pathname, errorMessage));
  };

  const getSubtitle = () => {

    if (isUnverifiedBusiness) {
      return 'Please confirm the below details about your business';
    }

    if (isDetailsCaptured(formDetails, businessDetailsKeys).result) {
      return 'Please confirm the below details about your business';
    }

    return 'Please accurately enter all your business details to streamline your funding application.';
  };

  const isNextButton = () => {
    sessionStorage.removeItem('businessName');
    sessionStorage.removeItem('vatNumber');
    if (isDetailsCaptured(formDetails, businessDetailsKeys).result) {
      return true;
    } else if (verifyBusinessName && (verifyRegNumber || verifyVatNumber)) {
      return true;
    }
    return false;
  };

  const onBackClick = () => {
    track(events.businessDetails1.formEnd('back'));
    sessionStorage.removeItem('businessName');
    sessionStorage.removeItem('vatNumber');
    window.location.pathname = ApplicationRoutes.businessList;
    backClick();
  };

  const handleRadioGroupChange = (event) => {
    setSharedDirectors(event);
  };

  return (
    <div className={styles.container}>
      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          onSubmit={formSubmit}
          validate={(values) => {

            if (isUnverifiedBusiness) {
              return {};
            }

            try {
              businessDetailsValidationSchema.validateSync(values, {
                abortEarly: false,
                context: { businessType, formattedList }
              });
              return {};
            } catch (error) {
              return error.inner.reduce((acc, err) => {
                acc[err.path] = err.message;
                return acc;
              }, {});
            }
          }}
          validateOnBlur={true}
          validateOnMount={true}
          innerRef={formRef}

        >
          {({ values, errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);
            return (
              <Form style={FormContainer} onFocus={handleFormFocus}>
                <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />
                <FormBuilder
                  fields={getFormFields(businessType, isUnverifiedBusiness, formattedList, formDetails)}
                  title="Please add your business details"
                  subtitle={getSubtitle()}
                  isMobile={isMobile}
                  values={values}
                  FormField={FormField}
                  errors={errors}
                  personalAddress={personalDetails?.address}
                  submitState={submitState}
                  setSubmitState={setSubmitState}
                  backClick={onBackClick}
                  isDetailsCaptured={isNextButton}
                  submissionStates={submissionStates}
                  nextClick={nextClick}
                  touched={touched}
                  isVatNumberPreFetched={isVatNumberPreFetched}
                  isVatOptional={businessType !== 'Sole Proprietorship'}
                  handleRadioGroupChange={(e) => handleRadioGroupChange(e)}
                  sharedDirectorsValue={sharedDirectors}
                  businessList={formattedList}
                />
              </Form>
            );
          }}
        </Formik>
      </ThemeProvider>
      <SupportPopup />
    </div>
  );
};
